<template>
  <v-card
    flat
    class="pa-4"
  >
    <v-card-title class="px-0">
      <v-icon left>
        mdi-wifi
      </v-icon> WiFi management
      <v-spacer />
      <v-btn
        text
        :disabled="loading || !canManage"
        @click="refresh"
      >
        Refresh results
      </v-btn>
    </v-card-title>
    <v-alert
      v-if="!canManage"
      dense
      text
      type="info"
    >
      WiFi management requires a User, Admin or SuperUser account.
    </v-alert>
    <template v-else>
      <p class="text-body-2">
        {{ isGroup ? 'Commands target every effective member of this group.' : 'Request the device’s WiFi status or manage its connection.' }} Offline commands wait for reconnection until they expire. Dispatched commands are awaiting a device report.
      </p>
      <v-alert
        v-if="error"
        dense
        text
        type="error"
      >
        {{ error }}
      </v-alert>
      <v-alert
        v-if="notice"
        dense
        text
        type="info"
      >
        {{ notice }}
      </v-alert>
      <v-simple-table
        v-if="isGroup && submissionDevices.length"
        class="mb-4"
      >
        <thead><tr><th>Batch target</th><th>Command</th><th>Submission status</th><th>Error</th></tr></thead>
        <tbody>
          <tr
            v-for="device in submissionDevices"
            :key="device.deviceId"
          >
            <td>{{ device.macAddress || device.deviceId }}</td><td>{{ device.commandId || '—' }}</td><td>{{ device.status }}</td><td>{{ device.error || '—' }}</td>
          </tr>
        </tbody>
      </v-simple-table>
      <v-row align="center">
        <v-col
          cols="12"
          sm="4"
          md="3"
        >
          <v-text-field
            v-model.number="ttlMinutes"
            type="number"
            min="1"
            max="1440"
            label="Command expiry (minutes)"
            hint="1–1440 minutes; default 10"
            persistent-hint
            :disabled="!!busy"
          />
        </v-col>
        <v-col
          cols="12"
          sm="8"
          md="9"
          class="wifi-actions"
        >
          <v-btn
            outlined
            :disabled="!!busy || !targetId"
            :loading="busy === 'status'"
            @click="send('status')"
          >
            Request status
          </v-btn>
          <v-btn
            v-if="!isGroup"
            outlined
            :disabled="!!busy || !targetId"
            :loading="busy === 'saved-networks'"
            @click="send('saved-networks')"
          >
            Request saved networks
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!!busy || !targetId"
            @click="openConnect()"
          >
            Connect
          </v-btn>
          <v-btn
            outlined
            color="error"
            :disabled="!!busy || !targetId"
            @click="disconnectDialog = true"
          >
            Disconnect
          </v-btn>
          <v-btn
            v-if="!isGroup"
            text
            :disabled="!!busy || !targetId"
            @click="tokenDialog = true"
          >
            Agent token
          </v-btn>
        </v-col>
      </v-row>
      <v-alert
        v-if="latestStatus"
        outlined
        type="info"
        class="mt-4"
      >
        Last reported: {{ latestStatus.report.status.connected ? 'Connected' : 'Disconnected' }} · WiFi {{ latestStatus.report.status.wifiEnabled ? 'enabled' : 'disabled' }}
        <br>SSID: {{ latestStatus.report.status.ssid == null ? 'Unavailable' : latestStatus.report.status.ssid }} · IP: {{ latestStatus.report.status.ipAddress || 'Unavailable' }} · Internet validated: {{ booleanLabel(latestStatus.report.status.validatedInternet) }}
        <br>Command #{{ latestStatus.commandId }} · {{ formatDate(latestStatus.acknowledgedAt) }}
      </v-alert>
      <div
        v-if="latestNetworks"
        class="mt-4"
      >
        <h3 class="text-subtitle-1">
          Last reported saved networks · command #{{ latestNetworks.commandId }}
        </h3>
        <v-simple-table>
          <thead><tr><th>SSID</th><th>Security</th><th>Network ID</th><th /></tr></thead>
          <tbody>
            <tr
              v-for="(network, index) in latestNetworks.report.savedNetworks"
              :key="index"
            >
              <td class="wifi-ssid">
                {{ network.ssid }}
              </td><td>{{ network.security || 'Unknown' }}</td><td>{{ network.networkId }}</td>
              <td>
                <v-btn
                  small
                  text
                  color="primary"
                  :disabled="!!busy"
                  @click="openConnect(network)"
                >
                  Connect
                </v-btn>
              </td>
            </tr>
            <tr v-if="!latestNetworks.report.savedNetworks.length">
              <td colspan="4">
                The device reported no saved networks.
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
      <h3 class="text-subtitle-1 mt-5">
        {{ isGroup ? 'Recent group WiFi commands' : 'Recent WiFi commands' }}
      </h3>
      <p class="text-caption">
        Results refresh every 5 seconds while this panel is visible. Network changes can interrupt connectivity and delay reports.
      </p>
      <v-data-table
        :headers="headers"
        :items="rows"
        item-key="commandId"
        :loading="loading"
        :items-per-page="10"
        no-data-text="No WiFi commands found."
      >
        <template v-slot:item.status="{ item }">
          <v-chip
            small
            :color="statusColor(item.status)"
          >
            {{ item.status }}
          </v-chip>
        </template>
        <template v-slot:item.acknowledgedAt="{ item }">
          {{ formatDate(item.acknowledgedAt) }}
        </template>
        <template v-slot:item.expiresAt="{ item }">
          {{ formatDate(item.expiresAt) }}
        </template>
        <template v-slot:item.report="{ item }">
          <span v-if="item.report">{{ item.report.code || 'Report received' }}</span>
          <span v-else>{{ item.error || (['Succeeded', 'Failed'].includes(item.status) ? 'View device report' : ['Expired', 'Cancelled'].includes(item.status) ? 'No device report' : 'Awaiting report') }}</span>
          <v-btn
            small
            text
            color="primary"
            @click="showReport(item)"
          >
            Details
          </v-btn>
        </template>
      </v-data-table>
      <v-btn
        v-if="isGroup"
        text
        :to="`/device-groups/${groupId}/history`"
      >
        Full group history
      </v-btn>
    </template>

    <v-dialog
      v-model="connectDialog"
      max-width="600"
      :persistent="!!busy"
    >
      <v-card>
        <v-card-title>Connect {{ isGroup ? 'group' : 'device' }} to WiFi</v-card-title>
        <v-card-text>
          <v-alert
            v-if="formErrors.length"
            dense
            text
            type="error"
          >
            <div
              v-for="message in formErrors"
              :key="message"
            >
              {{ message }}
            </div>
          </v-alert>
          <v-select
            v-model="connectionMode"
            :items="connectionModes"
            label="Connection type"
            :disabled="!!busy || configLoading"
          />
          <v-alert
            v-if="connectionMode !== 'protected'"
            dense
            text
            type="info"
          >
            {{ form.useSavedNetwork ? 'Use the credentials already saved on the device. No password is sent.' : 'Connect to an open network without a password.' }}
          </v-alert>
          <v-autocomplete
            v-if="!isGroup"
            :value="selectedSavedNetwork"
            :items="savedNetworkOptions"
            item-text="label"
            return-object
            label="Fetched saved WiFi networks"
            :loading="loading || savedNetworksLoading"
            :disabled="!!busy || configLoading"
            no-data-text="No saved networks reported yet. Request them below or enter an SSID."
            @change="selectSavedNetwork"
          />
          <v-btn
            v-if="!isGroup"
            small
            text
            color="primary"
            :loading="savedNetworksLoading"
            :disabled="!!busy || configLoading || savedNetworksLoading"
            @click="requestSavedNetworks"
          >
            Refresh saved networks
          </v-btn>
          <p
            v-if="savedNetworksCommandId"
            class="text-caption"
          >
            Waiting for the device's saved networks report (command #{{ savedNetworksCommandId }}). Offline devices report after reconnecting.
          </p>
          <p
            v-if="savedNetworksNotice"
            class="text-caption"
          >
            {{ savedNetworksNotice }}
          </p>
          <v-alert
            v-if="savedNetworksError"
            dense
            text
            type="warning"
          >
            {{ savedNetworksError }}
          </v-alert>
          <v-btn
            v-if="!isGroup || companyId"
            outlined
            small
            color="primary"
            class="my-3"
            :loading="configLoading"
            :disabled="!!busy || configLoading || (!companyId && isGroup)"
            @click="loadCompanyDefault"
          >
            Load company default WiFi
          </v-btn>
          <p
            v-if="!companyId && isGroup"
            class="text-caption"
          >
            Company default WiFi is available only when every device in this group belongs to the same company.
          </p>
          <p
            v-if="companyConfigNotice"
            class="text-caption"
          >
            {{ companyConfigNotice }}
          </p>
          <v-btn
            v-if="companyConfigNotice && form.security === null"
            small
            text
            color="primary"
            @click="connectionMode = 'saved'"
          >
            Use existing saved profile
          </v-btn>
          <v-text-field
            v-model="form.ssid"
            label="SSID"
            hint="Case and spaces are preserved; up to 32 UTF-8 bytes"
            persistent-hint
            :disabled="!!busy || configLoading"
          />
          <v-text-field
            v-if="form.useSavedNetwork && !isGroup"
            v-model="form.networkId"
            type="number"
            min="0"
            label="Saved network ID (optional)"
            hint="Choose a specific configuration when SSIDs are duplicated"
            persistent-hint
            :disabled="!!busy || configLoading"
          />
          <p
            v-if="form.useSavedNetwork && isGroup"
            class="mt-3"
          >
            The SSID must already be saved on each device. Duplicate matches are rejected by the agent.
          </p>
          <template v-if="!form.useSavedNetwork">
            <v-select
              v-model="form.security"
              :items="securityOptions"
              label="Security"
              :disabled="!!busy || configLoading"
              @change="form.password = ''"
            />
            <p class="text-caption">
              New profiles: Open, WPA/WPA2-Personal and WPA3-Personal. Other security types require an existing saved profile; this server cannot provision them.
            </p>
            <v-text-field
              v-if="form.security !== 'OPEN'"
              v-model="form.password"
              type="password"
              autocomplete="new-password"
              label="Password (required for a new protected network)"
              :disabled="!!busy || configLoading"
            />
            <v-checkbox
              v-model="form.hidden"
              label="Hidden network"
              :disabled="!!busy || configLoading"
            />
          </template>
          <v-text-field
            v-model.number="form.timeoutSeconds"
            type="number"
            min="5"
            max="120"
            label="Connection verification timeout (seconds)"
            :disabled="!!busy || configLoading"
          />
          <p>Switching networks may take the {{ isGroup ? 'devices' : 'device' }} offline. Success is shown only after a verified device report.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer /><v-btn
            text
            :disabled="!!busy"
            @click="connectDialog = false"
          >
            Cancel
          </v-btn><v-btn
            color="primary"
            :loading="busy === 'connect'"
            :disabled="!!busy || configLoading || savedNetworksLoading"
            @click="connect"
          >
            Send connection command
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="disconnectDialog"
      max-width="500"
      :persistent="!!busy"
    >
      <v-card>
        <v-card-title>Disconnect {{ isGroup ? 'all group devices' : 'device' }}?</v-card-title>
        <v-card-text>
          <v-alert
            v-if="disconnectError"
            dense
            text
            type="error"
          >
            {{ disconnectError }}
          </v-alert>
          This command may disconnect {{ isGroup ? 'the devices' : 'the device' }} and disable WiFi. Remote access may be lost until WiFi is re-enabled or another connection is available. Manual access to {{ isGroup ? 'the devices' : 'the device' }} may be required to restore connectivity.
        </v-card-text>
        <v-card-actions>
          <v-spacer /><v-btn
            text
            :disabled="!!busy"
            @click="disconnectDialog = false"
          >
            Cancel
          </v-btn><v-btn
            color="error"
            :loading="busy === 'disconnect'"
            :disabled="!!busy"
            @click="send('disconnect')"
          >
            Disconnect
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="reportDialog"
      max-width="600"
    >
      <v-card>
        <v-card-title>WiFi command #{{ detail && detail.commandId }}</v-card-title>
        <v-card-text v-if="detail">
          <p>Status: {{ detail.status }} · {{ detail.error || (detail.report && detail.report.code) }}</p>
          <p
            v-if="detailError"
            class="error--text"
          >
            {{ detailError }}
          </p>
          <v-progress-linear
            v-if="detailLoading"
            indeterminate
          />
          <template v-if="detail.report">
            <p v-if="detail.report.status">
              SSID: <span class="wifi-ssid">{{ detail.report.status.ssid == null ? 'Unavailable' : detail.report.status.ssid }}</span><br>Connected: {{ booleanLabel(detail.report.status.connected) }}<br>WiFi enabled: {{ booleanLabel(detail.report.status.wifiEnabled) }}<br>IP: {{ detail.report.status.ipAddress || 'Unavailable' }}<br>Internet validated: {{ booleanLabel(detail.report.status.validatedInternet) }}
            </p>
            <div v-if="detail.report.savedNetworks">
              <p v-if="!detail.report.savedNetworks.length">
                No saved networks.
              </p>
              <p
                v-for="(network, index) in detail.report.savedNetworks"
                :key="index"
              >
                <span class="wifi-ssid">{{ network.ssid }}</span> · {{ network.security || 'Unknown security' }} · ID {{ network.networkId }}
              </p>
            </div>
          </template>
          <p v-else-if="!detailLoading">
            No readable device report is available.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer /><v-btn
            text
            @click="reportDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="tokenDialog"
      max-width="600"
      :persistent="!!busy"
    >
      <v-card>
        <v-card-title>Device WiFi agent token</v-card-title>
        <v-card-text>
          <p>Issue a device-bound token valid for 30 minutes. Deliver it through your authenticated enrollment or management channel.</p>
          <v-alert
            v-if="tokenError"
            dense
            text
            type="error"
          >
            {{ tokenError }}
          </v-alert>
          <template v-if="agentToken">
            <v-textarea
              :value="agentToken.accessToken"
              readonly
              label="Access token"
              autocomplete="off"
            />
            <p>Expires: {{ formatDate(agentToken.expiresAt) }} · Hub: {{ agentToken.hubUrl }}</p>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer /><v-btn
            text
            :disabled="!!busy"
            @click="tokenDialog = false"
          >
            Close
          </v-btn><v-btn
            color="primary"
            :loading="busy === 'token'"
            :disabled="!!busy"
            @click="issueToken"
          >
            Issue token
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
  import Wifi from '@/services/WifiManagementDataService'
  import Groups from '@/services/DeviceGroupsDataService'
  import Commands from '@/services/DeviceCommandsDataService'
  import { wifiActions, parseWifiReport, validateWifiConnect, wifiConnectPayload, companyWifiForm, wifiSecurityOptions } from '@/util/wifiManagement'

  const emptyForm = () => ({ ssid: '', password: '', security: 'WPA2', hidden: false, useSavedNetwork: false, networkId: null, timeoutSeconds: 30 })

  export default {
    name: 'WifiManagement',
    props: {
      deviceId: { type: [Number, String], default: null },
      deviceMacAddress: { type: String, default: '' },
      groupId: { type: [Number, String], default: null },
      companyId: { type: [Number, String], default: null },
      active: { type: Boolean, default: true },
    },
    data: () => ({
      rows: [],
      loading: false,
      busy: '',
      error: '',
      notice: '',
      ttlMinutes: 10,
      submissionDevices: [],
      connectDialog: false,
      configLoading: false,
      companyConfigNotice: '',
      savedNetworksLoading: false,
      savedNetworksError: '',
      savedNetworksCommandId: null,
      savedNetworksNotice: '',
      disconnectDialog: false,
      disconnectError: '',
      form: emptyForm(),
      formErrors: [],
      reportDialog: false,
      detail: null,
      detailLoading: false,
      detailError: '',
      tokenDialog: false,
      agentToken: null,
      tokenError: '',
      timer: null,
      generation: 0,
    }),
    computed: {
      securityOptions () { return wifiSecurityOptions },
      canManage () { return this.$store.getters['authUser/canManageWifi'] },
      isGroup () { return !!this.groupId },
      targetId () { return this.groupId || this.deviceId },
      targetKey () { return `${this.isGroup ? 'group' : 'device'}:${this.targetId}` },
      reportedRows () {
        return this.rows.filter(row => row.report).slice().sort((a, b) =>
          String(b.acknowledgedAt || '').localeCompare(String(a.acknowledgedAt || '')) || b.commandId - a.commandId)
      },
      latestStatus () { return !this.isGroup && this.reportedRows.find(row => row.report.status) },
      latestNetworks () { return !this.isGroup && this.reportedRows.find(row => row.report.savedNetworks !== null) },
      connectionModes () {
        return [
          { text: 'New protected network (password)', value: 'protected' },
          { text: 'Open network (no password)', value: 'open' },
          { text: 'Saved network (no password)', value: 'saved' },
        ]
      },
      connectionMode: {
        get () { return this.form.useSavedNetwork ? 'saved' : this.form.security === 'OPEN' ? 'open' : 'protected' },
        set (mode) {
          this.clearCredentials()
          this.form.useSavedNetwork = mode === 'saved'
          this.form.security = mode === 'open' ? 'OPEN' : 'WPA2'
          this.formErrors = []
        },
      },
      savedNetworkOptions () {
        const networks = this.latestNetworks ? this.latestNetworks.report.savedNetworks : []
        return networks.map(network => ({
          ...network,
          label: this.isGroup ? `${network.ssid} (reported by group devices)` : `${network.ssid} (${network.security || 'Unknown security'}, ID ${network.networkId})`,
        }))
      },
      selectedSavedNetwork () {
        return this.savedNetworkOptions.find(network => network.ssid === this.form.ssid && (this.isGroup || (this.form.networkId !== null && this.form.networkId !== '' && network.networkId === Number(this.form.networkId)))) || null
      },
      headers () {
        return [
          { text: 'Command', value: 'commandId' },
          ...(this.isGroup ? [{ text: 'Device', value: 'deviceId' }, { text: 'Batch', value: 'batchId' }] : []),
          { text: 'Action', value: 'action' }, { text: 'Status', value: 'status' },
          { text: 'Reported at', value: 'acknowledgedAt' }, { text: 'Expires', value: 'expiresAt' },
          { text: 'Result', value: 'report', sortable: false },
        ]
      },
    },
    watch: {
      active (value) { if (!value) this.clearDialogs(); this.startPolling() },
      canManage (value) {
        if (!value) { this.generation++; this.loading = false; this.rows = []; this.submissionDevices = []; this.clearDialogs() }
        this.startPolling()
      },
      targetKey () {
        this.generation++
        this.loading = false
        this.rows = []
        this.savedNetworksCommandId = null
        this.savedNetworksNotice = ''
        this.submissionDevices = []
        this.error = this.notice = ''
        this.connectDialog = this.disconnectDialog = this.reportDialog = this.tokenDialog = false
        this.form = emptyForm()
        this.agentToken = null
        this.startPolling()
      },
      connectDialog (open) { if (!open) { this.form.password = ''; this.companyConfigNotice = '' } },
      companyId () { this.clearDialogs(); this.form = emptyForm() },
      disconnectDialog () { this.disconnectError = '' },
      tokenDialog (open) { if (!open) { this.agentToken = null; this.tokenError = '' } },
    },
    mounted () {
      document.addEventListener('visibilitychange', this.startPolling)
      this.startPolling()
    },
    beforeDestroy () {
      this.generation++
      clearInterval(this.timer)
      document.removeEventListener('visibilitychange', this.startPolling)
    },
    methods: {
      clearDialogs () {
        this.connectDialog = this.disconnectDialog = this.reportDialog = this.tokenDialog = false
        this.form.password = ''
        this.agentToken = null
        this.savedNetworksCommandId = null
        this.savedNetworksNotice = ''
        this.savedNetworksError = this.companyConfigNotice = ''
      },
      booleanLabel (value) { return value === true ? 'Yes' : value === false ? 'No' : 'Unknown' },
      formatDate (value) { return value ? this.$format.dateTime(value) : '—' },
      statusColor (status) { return ({ Succeeded: 'success', Failed: 'error', Expired: 'warning', Cancelled: 'grey', Queued: 'warning', Dispatched: 'info' })[status] || 'grey' },
      startPolling () {
        clearInterval(this.timer)
        if (!this.active || !this.canManage || !this.targetId || document.hidden) return
        this.refresh()
        this.timer = setInterval(() => this.refresh(), 5000)
      },
      describeError (error) {
        if (error.code === 'ECONNABORTED') return 'The request took too long. Please try again.'
        const data = error.response && error.response.data
        if (error.response && error.response.status === 403) {
          return (data && (data.message || data.detail)) || 'The API server denied WiFi permission (403). User accounts require the updated backend WiFi access policy. If that update is deployed, verify that this device is within your account’s company scope.'
        }
        if (data && data.errors) return Object.values(data.errors).flat().join(' ')
        return (data && (data.message || data.detail || data.title)) || 'WiFi request failed. Please try again.'
      },
      normalizeRow (row) {
        return {
          commandId: row.commandId,
          deviceId: row.deviceId,
          action: row.action || row.type,
          status: row.status || row.deliveryStatus,
          acknowledgedAt: row.acknowledgedAt,
          expiresAt: row.expiresAt,
          error: row.error || row.lastDeliveryError,
          batchId: row.batchId || row.commandBatchId,
          report: parseWifiReport(row.result || row.devicePayload),
        }
      },
      async refresh () {
        if (this.loading || !this.targetId || !this.canManage) return
        const generation = this.generation
        this.loading = true
        try {
          const response = this.isGroup ? await Groups.getCommandHistory(this.groupId, { limit: 200 }) : await Wifi.results(this.deviceId)
          if (generation !== this.generation) return
          this.rows = (Array.isArray(response.data) ? response.data : [])
            .map(this.normalizeRow).filter(row => wifiActions.includes(row.action))
            .sort((a, b) => b.commandId - a.commandId)
          if (generation !== this.generation) return
          if (this.savedNetworksCommandId) {
            const command = this.rows.find(row => row.commandId === this.savedNetworksCommandId)
            if (command && ['Succeeded', 'Failed', 'Expired', 'Cancelled'].includes(command.status)) {
              this.savedNetworksCommandId = null
              this.savedNetworksError = command.status === 'Succeeded' ? '' : command.error || `Saved networks request ${command.status.toLowerCase()}.`
            }
          }
          this.error = ''
        } catch (error) {
          if (generation === this.generation) this.error = this.describeError(error)
        } finally {
          if (generation === this.generation) this.loading = false
        }
      },
      validExpiry () {
        if (!Number.isInteger(Number(this.ttlMinutes)) || this.ttlMinutes < 1 || this.ttlMinutes > 1440) {
          this.error = 'Command expiry must be 1 to 1440 minutes.'
          return false
        }
        return true
      },
      async openConnect (network) {
        const generation = this.generation
        // Resolve only the company ID in advance; credentials load on demand.
        if (!this.isGroup && !this.companyId) this.resolveCompanyId().catch(() => {})
        this.form = network ? { ...emptyForm(), ssid: network.ssid, useSavedNetwork: true, networkId: this.isGroup ? null : network.networkId } : emptyForm()
        this.formErrors = []
        this.connectDialog = true
        this.companyConfigNotice = ''
        this.savedNetworksError = ''
        await this.refresh()
        if (generation !== this.generation || !this.connectDialog) return
        if (this.isGroup) return
        const pending = this.rows.find(row => row.action === 'GetSavedWifiNetworks' && ['Queued', 'Dispatched'].includes(row.status))
        if (!this.isGroup && pending) this.savedNetworksCommandId = pending.commandId
        if (!pending && !this.savedNetworkOptions.length && !(this.latestNetworks && !this.isGroup)) this.requestSavedNetworks()
      },
      selectSavedNetwork (network) {
        if (!network) return
        this.form = { ...emptyForm(), timeoutSeconds: this.form.timeoutSeconds, ssid: network.ssid, useSavedNetwork: true, networkId: this.isGroup ? null : network.networkId }
        this.formErrors = []
        this.companyConfigNotice = ''
      },
      async requestSavedNetworks () {
        if (this.isGroup || !this.targetId || !this.canManage || this.busy || this.savedNetworksLoading || !this.validExpiry()) return
        const generation = this.generation
        this.savedNetworksLoading = true
        this.savedNetworksError = ''
        try {
          const response = await Wifi.send(this.targetId, this.isGroup, 'saved-networks', { ttlMinutes: Number(this.ttlMinutes) })
          if (generation !== this.generation) return
          this.savedNetworksCommandId = response.data.commandId
          this.savedNetworksNotice = this.isGroup ? `Saved networks requested for ${response.data.targetCount} devices. Reported SSIDs will appear as devices respond; a network may not be saved on every member.` : ''
          await this.refresh()
        } catch (error) {
          if (generation === this.generation) this.savedNetworksError = this.describeError(error)
        } finally { this.savedNetworksLoading = false }
      },
      async resolveCompanyId () {
        if (this.companyId) return this.companyId
        if (this.isGroup) return null
        const key = `${this.deviceId}:${this.deviceMacAddress || ''}`
        if (this._companyLookup && this._companyLookup.key === key && Date.now() - this._companyLookup.startedAt < 30000) return this._companyLookup.promise
        const lookup = { key, startedAt: Date.now() }
        lookup.promise = (async () => {
          if (this.deviceMacAddress) {
            const response = await Wifi.companyForDevice(this.deviceMacAddress)
            const result = response.data
            return result && result.success && result.data && result.data.company ? result.data.company.id : null
          }
          const response = await Wifi.device(this.deviceId)
          return response.data && response.data.companyId
        })().catch(error => {
          if (this._companyLookup === lookup) this._companyLookup = null
          throw error
        })
        this._companyLookup = lookup
        return lookup.promise
      },
      async loadCompanyDefault () {
        if ((!this.companyId && this.isGroup) || !this.targetId || !this.canManage || this.busy || this.configLoading) return
        const scopeCompanyId = this.companyId
        const generation = this.generation
        const form = this.form
        let stage = 'device company lookup'
        this.configLoading = true
        this.companyConfigNotice = ''
        this.formErrors = []
        try {
          const companyId = await this.resolveCompanyId()
          if (generation !== this.generation || !this.connectDialog || this.form !== form) return
          if (!companyId) {
            this.formErrors = ['This device is not assigned to a company with a default WiFi configuration.']
            return
          }
          stage = 'company WiFi configuration'
          const response = await Wifi.companyDefault(companyId)
          if (generation !== this.generation || !this.connectDialog || this.form !== form || this.companyId !== scopeCompanyId || !this.canManage) return
          const result = response.data
          if (!result || !result.success || !result.data) {
            this.formErrors = ['No active default WiFi configuration was found for this company.']
            return
          }
          this.form = { ...emptyForm(), timeoutSeconds: form.timeoutSeconds, ...companyWifiForm(result.data) }
          this.companyConfigNotice = this.form.security
            ? 'Company default SSID, security and password loaded. Review before connecting.'
            : 'Company SSID and password loaded. The stored encryption is not supported by WiFi control; select the appropriate security before connecting.'
        } catch (error) {
          if (generation === this.generation && this.connectDialog && this.form === form && this.companyId === scopeCompanyId) {
            const message = error.response || error.code === 'ECONNABORTED' ? this.describeError(error) : error.message || 'Request failed.'
            this.formErrors = [`Unable to load ${stage}: ${message}`]
          }
        } finally { this.configLoading = false }
      },
      clearCredentials () { this.form.password = ''; this.form.networkId = null },
      async connect () {
        if (this.configLoading || this.savedNetworksLoading) return
        this.formErrors = validateWifiConnect(this.form, this.isGroup)
        if (!this.validExpiry()) this.formErrors.push(this.error)
        if (this.formErrors.length) return
        await this.send('connect', wifiConnectPayload(this.form, this.ttlMinutes, this.isGroup))
      },
      async send (action, payload) {
        if ((this.isGroup && action === 'saved-networks') || this.busy || !this.canManage || !this.targetId || !this.validExpiry()) return
        const generation = this.generation
        this.busy = action
        this.error = ''
        try {
          const response = await Wifi.send(this.targetId, this.isGroup, action, payload || { ttlMinutes: Number(this.ttlMinutes) })
          if (generation !== this.generation) return
          const result = response.data
          this.submissionDevices = this.isGroup ? result.devices || [] : []
          this.notice = this.isGroup
            ? `Batch ${result.batchId}: ${result.targetCount} targets, ${result.dispatchedCount} dispatched, ${result.queuedCount} queued, ${result.failedCount} failed. Await device reports for confirmed outcomes.`
            : `Command #${result.commandId}: ${result.status}. Await a device report for a confirmed outcome.${result.error ? ' ' + result.error : ''}`
          this.connectDialog = this.disconnectDialog = false
          this.form.password = ''
          await this.refresh()
        } catch (error) {
          if (generation === this.generation) {
            this.error = this.describeError(error)
            if (action === 'connect') this.formErrors = [this.error]
            if (action === 'disconnect') this.disconnectError = this.error
          }
        } finally { this.busy = '' }
      },
      async showReport (row) {
        const generation = this.generation
        this.detail = row
        this.detailError = ''
        this.reportDialog = true
        if (!this.isGroup) return
        this.detailLoading = true
        try {
          const response = await Commands.get(row.commandId)
          if (generation === this.generation && this.detail.commandId === row.commandId) this.detail = this.normalizeRow(response.data)
        } catch (error) {
          if (generation === this.generation) this.detailError = this.describeError(error)
        } finally { this.detailLoading = false }
      },
      async issueToken () {
        if (this.busy || this.isGroup || !this.canManage) return
        const generation = this.generation
        this.busy = 'token'
        this.tokenError = ''
        this.agentToken = null
        try {
          const response = await Wifi.token(this.deviceId)
          if (generation === this.generation && this.tokenDialog) this.agentToken = response.data
        } catch (error) {
          if (generation === this.generation) this.tokenError = this.describeError(error)
        } finally { this.busy = '' }
      },
    },
  }
</script>

<style scoped>
.wifi-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.wifi-ssid { white-space: pre-wrap; overflow-wrap: anywhere; }
</style>
